import { useContext, useCallback, useMemo, Fragment, forwardRef } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { Icon, Popup, Table, Checkbox } from "semantic-ui-react";

import { Config } from "../../../config/api";
import {
	currencyCodeToSymbol,
	formatFrequencyCap,
	formatNumber,
	getApiDate,
} from "../../../libs/common_utils";
import { isNewTabClick } from "../../../libs/component_utils";
import CampaignGridContext from "../index/context";
import FlightPacingPopup from "./FlightPacingPopup";
import DailyPacingPopup from "./DailyPacingPopup";
import { formatCurrencyByRoundedRules } from "./CampaignUtils";

/**
 * Generate grid item
 * @param {object} item
 * @param {boolean} item.cellVisible
 * @param {string} item.currency_code
 * @param {number} item.status
 * @param {string} item.title
 * @param {Date} item.start_date
 * @param {Date} item.end_date
 * @param {number} item.budget
 * @param {number} item.spend_to_date
 * @param {number} item.ctr
 * @param {number} item.clicks
 * @param {number} item.impressions
 * @param {number} item.id
 * @return {*}
 * @constructor
 */
export const CampaignGridItem = forwardRef((props, ref) => {
	const { item, selectedGrid, cellVisible } = props;
	const intl = useIntl();
	const context = useContext(CampaignGridContext);
	const currency = currencyCodeToSymbol(item.currency_code);
  const { setSelectedCampaigns, selectedCampaigns } = context;
	const moneyMetrics = useMemo(() => ['campaign_budget', 'spend_to_date', 'total_revenue', 'total_spend_cpa', 'total_spend_cpc', 'total_spend_cpm', 'goal_value', 'online_revenue', 'offline_revenue', 'total_spend_yesterday', 'budget', 'campaign_flight_pacing_remaining'], []);
	const dateMetrics = useMemo(() => ['start_date', 'end_date'], []);
	const numericMetrics = useMemo(() => ['clicks', 'total_conversions'], []);
	const wholeNumberMetrics = useMemo(() => ['impressions'], []);
	const nullishCurrencyFormat = value => {
		if (!value && value !== 0) return "-";
		return formatCurrencyByRoundedRules(value, item.currency_code);
	}

  const handleCheckboxChange = useCallback((e, { checked }) => {
    if (checked) {
      setSelectedCampaigns(prevCampaigns => [...prevCampaigns, item.id]);
    } else {
      setSelectedCampaigns(prevCampaigns => prevCampaigns.filter(id => id !== item.id));
    }
  }, [item.id]);
  const isCampaignChecked = selectedCampaigns.includes(item.id);

  const handleOnClick = useCallback((e) => {
    // Prevent the checkbox column from navigating to strategies page
    if (e.target.closest('.check-box-column')) {
      e.stopPropagation();
    } else {
      context.navigateToStrategiesPage(item.id);
    }
  }, [item.id]);

	const renderCampaignName = (data) => (
		<Table.Cell className="grid-item-col-name text-ellipsis campaign-name-cell" key="campaign_name">
			<Popup
				size="tiny"
				content={data.title}
				inverted
				trigger={
					<Link
						onClick={(e) => {
							e.stopPropagation();
						}}
						to={`/campaign/${data.id}/strategies`}
					>
						{data.title}
					</Link>
				}
			/>
		</Table.Cell>
	)

	const renderTableCell = (columnKey, value) => {
		if (!value && value !== 0 && typeof value !== 'boolean') return "-";
		let returnValue = value;
		if (typeof value === "boolean") {
			returnValue = value ? intl.formatMessage({
								id: "STATUS_ACTIVE",
								defaultMessage: "Active",
						  })
						: intl.formatMessage({
								id: "STATUS_INACTIVE",
								defaultMessage: "Inactive",
						  });
		} else if (moneyMetrics.includes(columnKey)) {
			returnValue = formatCurrencyByRoundedRules(value, item.currency_code, Config.defaultLocale, 2);
		} else if (dateMetrics.includes(columnKey)) {
			returnValue = getApiDate(value, Config.dateTimeFormat);
		} else if (numericMetrics.includes(columnKey)) {
			returnValue = formatNumber(value, Config.defaultLocale);
		} else if (wholeNumberMetrics.includes(columnKey)) {
			returnValue = formatNumber(Math.floor(value), Config.defaultLocale);
		} else if (columnKey === "campaign_frequency_type") {
			returnValue = formatFrequencyCap(
            intl,
            item.campaign_frequency_optimization,
            item.campaign_frequency_type,
            item.campaign_frequency_amount,
            item.campaign_frequency_interval
          );
		} else if (columnKey === 'goal_type') {
			returnValue = value.toUpperCase();
		}
		return returnValue;
	}

	return (
			<Table.Row
				className={
					"hoverable cursor-pointer" + (item.status ? "" : " inactive")
				}
				onClick={handleOnClick}
			>
        <Table.Cell className='check-box-column'>
          <Checkbox
            name={item.title}
            checked={isCampaignChecked}
            onChange={handleCheckboxChange}
          />
        </Table.Cell>
				{Object.keys(selectedGrid).map(column => {
					if (!selectedGrid[column]) return <Fragment key={column}></Fragment>;
					if (column === "title") {
						return renderCampaignName(item);
					}
					if (column === "daily_pacing") {
						return <Fragment key={column}><DailyPacingPopup data={item} nullishCurrencyFormat={nullishCurrencyFormat} /></Fragment>;
					}
					if (column === "flight_pacing") {
						return <Fragment key={column}><FlightPacingPopup data={item} nullishCurrencyFormat={nullishCurrencyFormat} /></Fragment>
					}
					return (
						<Table.Cell className={cellVisible(column)} key={column}>
							{renderTableCell(column, item[column])}
						</Table.Cell>
					)
				})}

				<Table.Cell data-clickable="0" textAlign="center">
					<a
						aria-label="View Chart"
						href={null}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							context.openChartAccordion();
							context.setCampaignSelected(item);
						}}
					>
						<Icon name="line graph" className="control" />
					</a>
					&nbsp;
					<a
						aria-label="Copy"
						href={null}
						onClick={(e) => {
							e.preventDefault();
							e.stopPropagation();
							context.setCampaignForDuplication(item);
						}}
					>
						<Icon name="copy outline" className="control" />
					</a>
					<a
						aria-label="Edit"
						href={context.getEditPageHref(item.id)}
						onClick={(e) => {
							if (isNewTabClick(e)) return;
							e.preventDefault();
							e.stopPropagation();
							context.navigateToEditPage(item.id);
						}}
					>
						<Icon name="edit" className="control" />
					</a>
				</Table.Cell>
			</Table.Row>
	);
});
